import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faFileAlt, faPlay, faStop, faCheckDouble, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ReactModal from 'react-modal';
import { toast } from 'sonner';
import { formatRut } from '../utils/formatRut';
import { Link } from 'react-router-dom';

ReactModal.setAppElement('#root');

const estadosMap = {
    aceptada: {
        label: 'Aceptada',
        dot: 'bg-emerald-500',
        badge: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
        card: 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200/80 hover:border-emerald-300',
        cardActive: 'ring-2 ring-emerald-400 border-emerald-400',
        count: 'text-emerald-700'
    },
    aceptada_con_devolucion: {
        label: 'Aceptada c/ devolución',
        dot: 'bg-teal-500',
        badge: 'bg-teal-50 text-teal-800 ring-teal-600/20',
        card: 'bg-gradient-to-br from-teal-50 to-white border-teal-200/80 hover:border-teal-300',
        cardActive: 'ring-2 ring-teal-400 border-teal-400',
        count: 'text-teal-700'
    },
    devolucion_en_proceso: {
        label: 'Devolución en proceso',
        dot: 'bg-cyan-500',
        badge: 'bg-cyan-50 text-cyan-800 ring-cyan-600/20',
        card: 'bg-gradient-to-br from-cyan-50 to-white border-cyan-200/80 hover:border-cyan-300',
        cardActive: 'ring-2 ring-cyan-400 border-cyan-400',
        count: 'text-cyan-700'
    },
    retenida: {
        label: 'Devolución retenida',
        dot: 'bg-rose-500',
        badge: 'bg-rose-50 text-rose-800 ring-rose-600/20',
        card: 'bg-gradient-to-br from-rose-50 to-white border-rose-200/80 hover:border-rose-300',
        cardActive: 'ring-2 ring-rose-400 border-rose-400',
        count: 'text-rose-700'
    },
    observada: {
        label: 'Observada',
        dot: 'bg-amber-500',
        badge: 'bg-amber-50 text-amber-900 ring-amber-600/20',
        card: 'bg-gradient-to-br from-amber-50 to-white border-amber-200/80 hover:border-amber-300',
        cardActive: 'ring-2 ring-amber-400 border-amber-400',
        count: 'text-amber-800'
    },
    rechazada: {
        label: 'Rechazada',
        dot: 'bg-red-500',
        badge: 'bg-red-50 text-red-800 ring-red-600/20',
        card: 'bg-gradient-to-br from-red-50 to-white border-red-200/80 hover:border-red-300',
        cardActive: 'ring-2 ring-red-400 border-red-400',
        count: 'text-red-700'
    },
    recibida: {
        label: 'Recibida',
        dot: 'bg-sky-500',
        badge: 'bg-sky-50 text-sky-800 ring-sky-600/20',
        card: 'bg-gradient-to-br from-sky-50 to-white border-sky-200/80 hover:border-sky-300',
        cardActive: 'ring-2 ring-sky-400 border-sky-400',
        count: 'text-sky-700'
    },
    pago_pendiente: {
        label: 'Pago pendiente',
        dot: 'bg-orange-500',
        badge: 'bg-orange-50 text-orange-900 ring-orange-600/20',
        card: 'bg-gradient-to-br from-orange-50 to-white border-orange-200/80 hover:border-orange-300',
        cardActive: 'ring-2 ring-orange-400 border-orange-400',
        count: 'text-orange-800'
    },
    no_declarada: {
        label: 'No declarada',
        dot: 'bg-slate-400',
        badge: 'bg-slate-50 text-slate-700 ring-slate-500/20',
        card: 'bg-gradient-to-br from-slate-50 to-white border-slate-200/80 hover:border-slate-300',
        cardActive: 'ring-2 ring-slate-400 border-slate-400',
        count: 'text-slate-600'
    },
    sin_clave: {
        label: 'Sin clave',
        dot: 'bg-zinc-400',
        badge: 'bg-zinc-100 text-zinc-700 ring-zinc-500/20',
        card: 'bg-gradient-to-br from-zinc-50 to-white border-zinc-200/80 hover:border-zinc-300',
        cardActive: 'ring-2 ring-zinc-400 border-zinc-400',
        count: 'text-zinc-600'
    },
    credencial_invalida: {
        label: 'Clave incorrecta',
        dot: 'bg-orange-400',
        badge: 'bg-orange-50 text-orange-900 ring-orange-500/20',
        card: 'bg-gradient-to-br from-orange-50/80 to-white border-orange-200/80 hover:border-orange-300',
        cardActive: 'ring-2 ring-orange-400 border-orange-400',
        count: 'text-orange-800'
    },
    persona_fallecida: {
        label: 'Persona fallecida',
        dot: 'bg-stone-500',
        badge: 'bg-stone-100 text-stone-800 ring-stone-500/20',
        card: 'bg-gradient-to-br from-stone-50 to-white border-stone-200/80 hover:border-stone-300',
        cardActive: 'ring-2 ring-stone-400 border-stone-400',
        count: 'text-stone-700'
    },
    rut_invalido: {
        label: 'RUT inválido',
        dot: 'bg-red-400',
        badge: 'bg-red-100 text-red-900 ring-red-500/20',
        card: 'bg-gradient-to-br from-red-50 to-white border-red-200/80 hover:border-red-300',
        cardActive: 'ring-2 ring-red-400 border-red-400',
        count: 'text-red-700'
    },
    no_hacer: {
        label: 'No hacer',
        dot: 'bg-violet-400',
        badge: 'bg-violet-50 text-violet-800 ring-violet-500/20',
        card: 'bg-gradient-to-br from-violet-50 to-white border-violet-200/80 hover:border-violet-300',
        cardActive: 'ring-2 ring-violet-400 border-violet-400',
        count: 'text-violet-700'
    },
    error: {
        label: 'Error',
        dot: 'bg-red-300',
        badge: 'bg-red-50 text-red-700 ring-red-500/20',
        card: 'bg-gradient-to-br from-red-50/70 to-white border-red-100 hover:border-red-200',
        cardActive: 'ring-2 ring-red-300 border-red-300',
        count: 'text-red-600'
    }
};

const fieldClass =
    'w-full h-11 px-3.5 text-sm text-slate-900 bg-white border border-slate-200 rounded-xl shadow-sm ' +
    'transition-all placeholder:text-slate-400 hover:border-slate-300 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-500';

const POLL_MS = 3000;
const REFRESH_MS = 3000;

const EstadoBadge = ({ estadoKey, size = 'sm' }) => {
    const info = estadosMap[estadoKey] || estadosMap.no_declarada;
    const sizeClass = size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2.5 py-0.5 text-xs';
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-md font-medium ring-1 ring-inset ${sizeClass} ${info.badge}`}>
            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${info.dot}`} />
            {info.label}
        </span>
    );
};

const EstadosRenta = () => {
    const { currentUser } = useContext(UserContext);
    const isAdmin = currentUser?.role === 'ADMIN';

    const [estados, setEstados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [años, setAños] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedEstado, setSelectedEstado] = useState('');
    const [search, setSearch] = useState('');
    const [isScrapingRunning, setIsScrapingRunning] = useState(false);
    const [scrapingProgress, setScrapingProgress] = useState(null);
    const [activeLog, setActiveLog] = useState(null);
    const [recentlyChangedRuts, setRecentlyChangedRuts] = useState(new Set());
    const [processingNombre, setProcessingNombre] = useState('');
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [modalRut, setModalRut] = useState(null);
    const [desdeRut, setDesdeRut] = useState('');
    const [selectedRuts, setSelectedRuts] = useState(new Set());
    const [consultandoRut, setConsultandoRut] = useState(null);
    const [clientesRenta, setClientesRenta] = useState([]);
    const [pinnedRuts, setPinnedRuts] = useState(() => new Set());
    const [showLogErrores, setShowLogErrores] = useState(false);
    const [showLogExitosos, setShowLogExitosos] = useState(false);
    const scrapingIntervalRef = useRef(null);
    const refreshIntervalRef = useRef(null);
    const estadosSnapshotRef = useRef(new Map());
    const changeFlashTimeoutRef = useRef(null);
    const selectedYearRef = useRef(selectedYear);
    const estadosRef = useRef([]);
    const activeLogIdRef = useRef(null);

    useEffect(() => {
        selectedYearRef.current = selectedYear;
    }, [selectedYear]);

    useEffect(() => {
        fetchEstados(false);
        generateYears();
    }, [selectedYear]);

    useEffect(() => {
        return () => {
            if (scrapingIntervalRef.current) clearInterval(scrapingIntervalRef.current);
            if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
            if (changeFlashTimeoutRef.current) clearTimeout(changeFlashTimeoutRef.current);
        };
    }, []);

    const pinRut = useCallback((rut) => {
        if (!rut) return;
        setPinnedRuts(prev => {
            if (prev.has(rut)) return prev;
            const next = new Set(prev);
            next.add(rut);
            return next;
        });
    }, []);

    const pinRuts = useCallback((ruts) => {
        if (!ruts?.length) return;
        setPinnedRuts(prev => {
            const next = new Set(prev);
            ruts.forEach(r => r && next.add(r));
            return next;
        });
    }, []);

    useEffect(() => {
        api.get('/listados/all')
            .then(r => setClientesRenta((r.data || []).filter(c => c.renta).sort((a, b) => a.nombre.localeCompare(b.nombre))))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setSelectedRuts(new Set());
    }, [selectedYear, selectedEstado, search]);

    const generateYears = () => {
        const currentYear = new Date().getFullYear();
        const yearList = [];
        for (let i = currentYear; i >= currentYear - 5; i--) yearList.push(i);
        setAños(yearList);
    };

    const fingerprintCliente = (cliente, year = selectedYearRef.current) => {
        const ea = cliente.estados?.find(e => e.año === year);
        return [
            cliente.rut,
            ea?.estado || 'no_declarada',
            ea?.mensajeSituacion || '',
            ea?.ultimoEvento?.fecha || '',
            ea?.ultimoEvento?.descripcion || '',
            cliente.ultimaActualizacion || ''
        ].join('|');
    };

    const fetchEstados = useCallback(async (silent = true) => {
        const year = selectedYearRef.current;
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const response = await api.get(`/estados-sii/${year}`);
            const newData = response.data || [];
            const changed = new Set();

            newData.forEach(cliente => {
                const fp = fingerprintCliente(cliente, year);
                const prev = estadosSnapshotRef.current.get(cliente.rut);
                if (prev && prev !== fp) changed.add(cliente.rut);
                estadosSnapshotRef.current.set(cliente.rut, fp);
            });

            if (changed.size > 0) {
                pinRuts([...changed]);
                setRecentlyChangedRuts(changed);
                if (changeFlashTimeoutRef.current) clearTimeout(changeFlashTimeoutRef.current);
                changeFlashTimeoutRef.current = setTimeout(() => setRecentlyChangedRuts(new Set()), 8000);
            }

            setEstados(newData);
            estadosRef.current = newData;
        } catch (error) {
            console.error('Error al cargar estados SII:', error);
            if (!silent) toast.error('Error al cargar estados de declaraciones');
        } finally {
            if (!silent) setLoading(false);
            setRefreshing(false);
        }
    }, [pinRuts]);

    const fetchLogActivo = useCallback(async (year) => {
        try {
            const res = await api.get(`/log-scraping/activo/${year}`);
            return res.data;
        } catch (err) {
            if (err.response?.status === 404) {
                try {
                    const listRes = await api.get('/log-scraping', {
                        params: { anio: year, estado: 'en_progreso', limit: 1 }
                    });
                    return listRes.data?.logs?.[0] || null;
                } catch {
                    return null;
                }
            }
            console.error('Error obteniendo log activo:', err);
            return null;
        }
    }, []);

    const aplicarLogAlEstado = useCallback((log) => {
        if (!log) return;
        setActiveLog(log);
        setScrapingProgress({
            procesados: log.procesados ?? 0,
            total: log.totalProcesar ?? 0
        });
        setProcessingNombre(log.empresaActual || '');
        if (log.empresaActual) {
            const enCurso = estadosRef.current.find(e => e.nombre === log.empresaActual);
            if (enCurso?.rut) pinRut(enCurso.rut);
        }
    }, [pinRut]);

    const finalizarSeguimiento = useCallback((log) => {
        if (scrapingIntervalRef.current) {
            clearInterval(scrapingIntervalRef.current);
            scrapingIntervalRef.current = null;
        }
        setIsScrapingRunning(false);
        setProcessingNombre('');
        activeLogIdRef.current = null;
        toast.success(log.estado === 'completado' ? 'Consulta completada' : 'Consulta finalizada');
    }, []);

    const pollScrapingLog = useCallback(async () => {
        const year = selectedYearRef.current;
        try {
            const log = await fetchLogActivo(year);

            if (!log) return;

            if (activeLogIdRef.current && log._id !== activeLogIdRef.current && log.estado === 'en_progreso') {
                activeLogIdRef.current = log._id;
            }

            aplicarLogAlEstado(log);
            if (!activeLogIdRef.current) activeLogIdRef.current = log._id;

            await fetchEstados(true);

            if (log.estado !== 'en_progreso') {
                finalizarSeguimiento(log);
                await fetchEstados(true);
            }
        } catch (err) {
            console.error('Error en polling scraping:', err);
        }
    }, [fetchLogActivo, aplicarLogAlEstado, fetchEstados, finalizarSeguimiento]);

    const iniciarSeguimiento = useCallback(async (logInicial = null) => {
        if (scrapingIntervalRef.current) clearInterval(scrapingIntervalRef.current);

        setIsScrapingRunning(true);
        setShowLogErrores(false);
        setShowLogExitosos(false);

        if (logInicial) {
            activeLogIdRef.current = logInicial._id;
            aplicarLogAlEstado(logInicial);
        } else {
            setScrapingProgress({ procesados: 0, total: '…' });
            setActiveLog(null);
            setProcessingNombre('');

            for (let i = 0; i < 15; i++) {
                const log = await fetchLogActivo(selectedYearRef.current);
                if (log) {
                    activeLogIdRef.current = log._id;
                    aplicarLogAlEstado(log);
                    break;
                }
                await new Promise(r => setTimeout(r, 2000));
            }
        }

        pollScrapingLog();
        scrapingIntervalRef.current = setInterval(pollScrapingLog, POLL_MS);
    }, [pollScrapingLog, fetchLogActivo, aplicarLogAlEstado]);

    // Refresco automático de tabla (y modal vía sync) mientras hay actividad
    useEffect(() => {
        const activo = isScrapingRunning || consultandoRut || modalIsOpen;
        if (!activo) {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
            return;
        }

        fetchEstados(true);
        if (!refreshIntervalRef.current) {
            refreshIntervalRef.current = setInterval(() => fetchEstados(true), REFRESH_MS);
        }

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [isScrapingRunning, consultandoRut, modalIsOpen, fetchEstados]);

    // Mantener modal sincronizado con datos frescos de la tabla
    useEffect(() => {
        if (!modalIsOpen || !modalRut) return;
        const updated = estados.find(e => e.rut === modalRut);
        if (updated) setSelectedCliente(updated);
    }, [estados, modalIsOpen, modalRut]);

    useEffect(() => {
        const detectarProcesoActivo = async () => {
            const log = await fetchLogActivo(selectedYear);
            if (log?.estado === 'en_progreso') {
                iniciarSeguimiento(log);
            }
        };
        detectarProcesoActivo();
    }, [selectedYear, iniciarSeguimiento, fetchLogActivo]);

    const filteredEstados = useMemo(() => {
        const list = estados.filter(estado => {
            const matchSearch = !search ||
                estado.nombre?.toLowerCase().includes(search.toLowerCase()) ||
                estado.rut?.includes(search);
            const añoEstado = estado.estados?.find(e => e.año === selectedYear);
            const matchEstado = !selectedEstado || añoEstado?.estado === selectedEstado;
            const isPinned = pinnedRuts.has(estado.rut);
            const isProcessingRow = consultandoRut === estado.rut ||
                (processingNombre && estado.nombre === processingNombre);
            return matchSearch && (matchEstado || isPinned || isProcessingRow);
        });

        return list.sort((a, b) => {
            const score = (item) => {
                let s = 0;
                if (consultandoRut === item.rut || (processingNombre && item.nombre === processingNombre)) s += 4;
                if (recentlyChangedRuts.has(item.rut)) s += 3;
                if (pinnedRuts.has(item.rut)) s += 2;
                return s;
            };
            return score(b) - score(a);
        });
    }, [estados, search, selectedEstado, selectedYear, pinnedRuts, consultandoRut, processingNombre, recentlyChangedRuts]);

    const filteredRuts = useMemo(() => filteredEstados.map(e => e.rut), [filteredEstados]);
    const allFilteredSelected = filteredRuts.length > 0 && filteredRuts.every(r => selectedRuts.has(r));

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            setSelectedRuts(new Set());
        } else {
            setSelectedRuts(new Set(filteredRuts));
        }
    };

    const toggleSelect = (rut) => {
        setSelectedRuts(prev => {
            const next = new Set(prev);
            if (next.has(rut)) next.delete(rut);
            else next.add(rut);
            return next;
        });
    };

    const handleDetenerScraping = () => {
        if (scrapingIntervalRef.current) {
            clearInterval(scrapingIntervalRef.current);
            scrapingIntervalRef.current = null;
        }
        setIsScrapingRunning(false);
        setScrapingProgress(null);
        setProcessingNombre('');
        activeLogIdRef.current = null;
        toast.info('Seguimiento detenido en pantalla. El proceso en servidor puede continuar.');
    };

    const handleCancelarScraping = async () => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Detener la consulta?',
            text: 'Se detendrá el scraping. Los datos ya procesados se conservan.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, detener',
            cancelButtonText: 'Continuar',
            confirmButtonColor: '#dc2626'
        });
        if (!isConfirmed) return;
        try {
            await api.post('/estados-sii/forzar-cancelar');
            toast.success('Consulta detenida');
            finalizarSeguimiento({ estado: 'cancelado' });
            await fetchEstados(true);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al cancelar');
        }
    };

    const buildConfirmHtml = (modo, count) => {
        const base = `Año base <strong>${selectedYear}</strong> (+ 2 años anteriores por empresa).`;
        if (modo === 'una') return `${base}<br>Empresa: <strong>${count}</strong>`;
        if (modo === 'filtradas') return `${base}<br>Empresas con estado <strong>${estadosMap[selectedEstado]?.label || selectedEstado}</strong>: <strong>${count}</strong>`;
        if (modo === 'seleccionadas') return `${base}<br>Empresas seleccionadas: <strong>${count}</strong>`;
        if (modo === 'desde') return `${base}<br>Desde: <strong>${count}</strong> hasta el final.`;
        return `${base}<br>Total empresas con renta: <strong>${count}</strong>`;
    };

    const handleEjecutarScraping = async (modo = 'todas') => {
        if (!isAdmin || isScrapingRunning) return;

        let payload = { año: selectedYear };
        let count = estados.length;
        let titulo = 'Consultar todas las empresas';

        if (modo === 'filtradas') {
            if (!selectedEstado) {
                toast.warning('Selecciona un estado en el filtro para consultar solo ese grupo');
                return;
            }
            payload.filtroEstado = selectedEstado;
            count = filteredEstados.length;
            titulo = `Consultar empresas «${estadosMap[selectedEstado]?.label}»`;
        } else if (modo === 'seleccionadas') {
            if (selectedRuts.size === 0) {
                toast.warning('Marca al menos una empresa en la tabla');
                return;
            }
            payload.ruts = [...selectedRuts];
            count = selectedRuts.size;
            titulo = 'Consultar empresas seleccionadas';
        } else if (modo === 'desde') {
            if (!desdeRut) {
                toast.warning('Elige una empresa de inicio');
                return;
            }
            payload.desdeRut = desdeRut;
            const idx = clientesRenta.findIndex(c => c.rut === desdeRut);
            count = idx >= 0 ? clientesRenta.length - idx : clientesRenta.length;
            titulo = 'Consultar desde empresa';
        }

        if (count === 0) {
            toast.info('No hay empresas que coincidan con el criterio');
            return;
        }

        const { isConfirmed } = await Swal.fire({
            title: titulo,
            html: `${buildConfirmHtml(modo, count)}<br><br><small class="text-gray-500">Todas se consultan en el SII; la BD solo se actualiza si el estado cambió.</small>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Ejecutar',
            cancelButtonText: 'Cancelar'
        });

        if (!isConfirmed) return;

        if (modo === 'seleccionadas') pinRuts([...selectedRuts]);
        else if (modo === 'filtradas') pinRuts(filteredEstados.map(e => e.rut));
        else if (modo === 'desde' && desdeRut) pinRut(desdeRut);

        try {
            await api.post('/estados-sii/ejecutar', payload);
            toast.info('Consulta iniciada en segundo plano');
            iniciarSeguimiento();
        } catch (error) {
            if (error.response?.status === 409) {
                toast.info('Ya hay una consulta en curso. Conectando al seguimiento…');
                iniciarSeguimiento();
                return;
            }
            Swal.fire('Error', error.response?.data?.error || error.message, 'error');
        }
    };

    const handleConsultarUna = async (cliente) => {
        if (!isAdmin || isScrapingRunning) return;
        const clienteId = cliente.clienteId?._id || cliente.clienteId;
        if (!clienteId) {
            toast.error('No se encontró el ID del cliente');
            return;
        }

        pinRut(cliente.rut);
        setConsultandoRut(cliente.rut);
        try {
            await api.post(`/estados-sii/actualizar-empresa/${clienteId}`, { año: selectedYear });
            toast.success(`Consulta completada: ${cliente.nombre}`);
            await fetchEstados(true);
            if (modalRut === cliente.rut) {
                const updated = (await api.get(`/estados-sii/${selectedYear}`)).data?.find(e => e.rut === cliente.rut);
                if (updated) setSelectedCliente(updated);
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al consultar empresa');
        } finally {
            setConsultandoRut(null);
        }
    };

    const openModal = (cliente) => {
        pinRut(cliente.rut);
        setModalRut(cliente.rut);
        setSelectedCliente(cliente);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSelectedCliente(null);
        setModalRut(null);
    };

    const clearPinned = () => setPinnedRuts(new Set());

    const resumenPorEstado = useMemo(() => {
        const counts = {};
        estados.forEach(e => {
            const ea = e.estados?.find(x => x.año === selectedYear);
            const st = ea?.estado || 'no_declarada';
            counts[st] = (counts[st] || 0) + 1;
        });
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([key, total]) => ({
                key,
                total,
                label: estadosMap[key]?.label || key,
                dot: estadosMap[key]?.dot || 'bg-slate-400',
                badge: estadosMap[key]?.badge || 'bg-slate-50 text-slate-700 ring-slate-500/20',
                card: estadosMap[key]?.card || 'bg-white border-slate-200',
                cardActive: estadosMap[key]?.cardActive || 'ring-2 ring-indigo-400',
                count: estadosMap[key]?.count || 'text-slate-700'
            }));
    }, [estados, selectedYear]);

    const ultimosProcesados = useMemo(() => {
        const detalle = activeLog?.exitososDetalle || [];
        return [...detalle].reverse().slice(0, 8);
    }, [activeLog]);

    const logStats = useMemo(() => {
        const log = activeLog;
        if (!log) {
            const proc = scrapingProgress?.procesados ?? 0;
            const total = scrapingProgress?.total;
            const totalNum = typeof total === 'number' ? total : 0;
            return {
                procesados: proc,
                total: totalNum,
                exitosos: 0,
                fallidos: 0,
                sinClave: 0,
                pendientes: Math.max(0, totalNum - proc)
            };
        }
        const total = log.totalProcesar ?? 0;
        const procesados = log.procesados ?? 0;
        return {
            procesados,
            total,
            exitosos: log.exitosos ?? 0,
            fallidos: log.fallidos ?? 0,
            sinClave: log.sinClave ?? 0,
            pendientes: Math.max(0, total - procesados)
        };
    }, [activeLog, scrapingProgress]);

    const progressPct = useMemo(() => {
        const { procesados, total } = logStats;
        if (!total) return 0;
        return Math.min(100, Math.round((procesados / total) * 100));
    }, [logStats]);

    if (loading) {
        return (
            <div className="min-h-[40vh] flex items-center justify-center">
                <div className="text-sm text-slate-500 tracking-wide">Cargando estados SII…</div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
            {/* Encabezado */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-slate-200 pb-6">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Operación renta</p>
                    <h1 className="text-2xl font-semibold text-slate-900">Estados de declaración SII</h1>
                    <p className="text-sm text-slate-500 mt-1">Monitoreo y consulta masiva al Servicio de Impuestos Internos</p>
                </div>
                {isAdmin && (
                    <Link
                        to="/monitor-sii"
                        className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-4 py-2 bg-white hover:bg-slate-50 transition-colors"
                    >
                        <FontAwesomeIcon icon={faSync} />
                        Monitor de ejecución
                    </Link>
                )}
            </div>

            {/* Resumen por estado — grid de cards */}
            {resumenPorEstado.length > 0 && (
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold text-slate-800">
                            Resumen año {selectedYear}
                        </h2>
                        <span className="text-xs text-slate-500">{estados.length} empresas · clic para filtrar</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {resumenPorEstado.map(({ key, total, label, dot, card, cardActive, count }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setSelectedEstado(prev => prev === key ? '' : key)}
                                className={`text-left rounded-xl border p-4 shadow-sm transition-all duration-200 ${card} ${
                                    selectedEstado === key ? cardActive + ' shadow-md scale-[1.02]' : ''
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dot}`} />
                                    <span className="text-xs font-medium text-slate-600 leading-tight">{label}</span>
                                </div>
                                <p className={`text-2xl font-bold tabular-nums leading-none ${count}`}>{total}</p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Panel en vivo durante consulta masiva */}
            {isScrapingRunning && (
                <div className="bg-white border border-indigo-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-white border-b border-indigo-100">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <FontAwesomeIcon icon={faSync} spin className="text-indigo-600" />
                                <span className="text-sm font-semibold text-slate-900">Consulta en curso</span>
                                {activeLog?.tipoEjecucion && (
                                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                        {activeLog.tipoEjecucion}
                                    </span>
                                )}
                                {refreshing && (
                                    <span className="text-xs text-indigo-600">actualizando tabla…</span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {activeLog?.fechaInicio && (
                                    <span className="text-xs text-slate-500 hidden sm:inline">
                                        Inicio: {new Date(activeLog.fechaInicio).toLocaleString('es-CL')}
                                    </span>
                                )}
                                <span className="text-sm font-bold tabular-nums text-indigo-700">
                                    {logStats.procesados} / {logStats.total || '…'}
                                    {logStats.total > 0 && (
                                        <span className="ml-1 font-normal text-slate-500">({progressPct}%)</span>
                                    )}
                                </span>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={handleCancelarScraping}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-red-700 bg-red-50 border border-red-200 hover:bg-red-100"
                                    >
                                        <FontAwesomeIcon icon={faStop} />
                                        Detener
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="mt-3 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-indigo-500 transition-all duration-500 rounded-full"
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>

                        {(activeLog?.empresaActual || activeLog?.accionActual || processingNombre) && (
                            <div className="mt-3 flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2.5 text-sm text-indigo-900">
                                <FontAwesomeIcon icon={faSync} spin className="text-indigo-500 shrink-0 mt-0.5 text-xs" />
                                <div className="min-w-0">
                                    {(activeLog?.empresaActual || processingNombre) && (
                                        <p className="font-semibold truncate">
                                            {activeLog?.empresaActual || processingNombre}
                                        </p>
                                    )}
                                    <p className="text-indigo-700/90 truncate">
                                        {activeLog?.accionActual || 'Procesando…'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {!activeLog && (
                            <p className="mt-3 text-sm text-slate-500">
                                Esperando que el servidor registre la consulta…
                            </p>
                        )}
                    </div>

                    <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-slate-100">
                        <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-center">
                            <p className="text-xl font-bold text-emerald-700 tabular-nums">{logStats.exitosos}</p>
                            <p className="text-[11px] font-medium text-emerald-800/70 uppercase tracking-wide">Exitosas</p>
                        </div>
                        <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-center">
                            <p className="text-xl font-bold text-red-600 tabular-nums">{logStats.fallidos}</p>
                            <p className="text-[11px] font-medium text-red-800/70 uppercase tracking-wide">Con error</p>
                        </div>
                        <div className="rounded-lg bg-orange-50 border border-orange-100 p-3 text-center">
                            <p className="text-xl font-bold text-orange-600 tabular-nums">{logStats.sinClave}</p>
                            <p className="text-[11px] font-medium text-orange-800/70 uppercase tracking-wide">Sin clave</p>
                        </div>
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
                            <p className="text-xl font-bold text-slate-700 tabular-nums">{logStats.pendientes}</p>
                            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Pendientes</p>
                        </div>
                    </div>

                    {activeLog?.logEntries?.length > 0 && (
                        <div className="px-5 py-3 border-b border-slate-100">
                            <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Log del proceso</p>
                            <div className="bg-slate-900 rounded-lg p-3 max-h-40 overflow-y-auto font-mono text-[11px] leading-relaxed">
                                {[...activeLog.logEntries].reverse().slice(0, 12).map((line, i) => (
                                    <div
                                        key={i}
                                        className={`py-0.5 ${
                                            line.includes('✅') ? 'text-green-400' :
                                            line.includes('❌') ? 'text-red-400' :
                                            line.includes('⚠️') ? 'text-yellow-400' :
                                            line.includes('⏳') || line.includes('🔄') || line.includes('📊') ? 'text-blue-300' :
                                            'text-gray-300'
                                        }`}
                                    >
                                        {line}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeLog?.errores?.length > 0 && (
                        <div className="px-5 py-3 border-b border-slate-100">
                            <button
                                type="button"
                                onClick={() => setShowLogErrores(v => !v)}
                                className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-800"
                            >
                                <FontAwesomeIcon icon={showLogErrores ? faChevronUp : faChevronDown} />
                                {activeLog.errores.length} error(es) registrado(s)
                            </button>
                            {showLogErrores && (
                                <div className="mt-2 max-h-36 overflow-y-auto rounded-lg border border-red-100 bg-red-50/50">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-red-50 sticky top-0">
                                            <tr>
                                                <th className="px-3 py-1.5 text-left font-medium text-red-800">Empresa</th>
                                                <th className="px-3 py-1.5 text-left font-medium text-red-800">Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...activeLog.errores].reverse().slice(0, 10).map((e, i) => (
                                                <tr key={i} className="border-t border-red-100">
                                                    <td className="px-3 py-1.5 text-slate-800">{e.nombreCliente}</td>
                                                    <td className="px-3 py-1.5 text-red-600">{e.error}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {ultimosProcesados.length > 0 && (
                        <div className="px-5 py-3">
                            <button
                                type="button"
                                onClick={() => setShowLogExitosos(v => !v)}
                                className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 mb-2"
                            >
                                <FontAwesomeIcon icon={showLogExitosos ? faChevronUp : faChevronDown} />
                                Últimas empresas procesadas ({ultimosProcesados.length})
                            </button>
                            {(showLogExitosos || ultimosProcesados.length <= 4) && (
                                <div className="flex flex-wrap gap-2">
                                    {ultimosProcesados.map((item, i) => {
                                        const est = item.estados?.find(e => e.año === selectedYear);
                                        return (
                                            <span
                                                key={`${item.rut}-${i}`}
                                                className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 max-w-full"
                                            >
                                                <span className="text-slate-700 truncate max-w-[160px]">{item.nombreCliente}</span>
                                                {est?.estado && <EstadoBadge estadoKey={est.estado} />}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="px-5 py-2 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[11px] text-slate-500">Actualizando cada {POLL_MS / 1000}s</span>
                        <Link to="/monitor-sii" className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800">
                            Ver monitor completo →
                        </Link>
                    </div>
                </div>
            )}

            {/* Panel filtros y acciones */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="p-4 sm:p-5 border-b border-slate-100">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-2">Año tributario</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                className={fieldClass}
                            >
                                {años.map(año => (
                                    <option key={año} value={año}>{año}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-2">Estado</label>
                            <select
                                value={selectedEstado}
                                onChange={(e) => setSelectedEstado(e.target.value)}
                                className={fieldClass}
                            >
                                <option value="">Todos los estados</option>
                                {Object.entries(estadosMap).map(([key, value]) => (
                                    <option key={key} value={key}>{value.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-2">Buscar</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Nombre o RUT…"
                                className={fieldClass}
                            />
                        </div>
                    </div>
                    <p className="mt-3 text-xs text-slate-500 flex flex-wrap items-center gap-2">
                        <span>
                            {filteredEstados.length} de {estados.length} empresas
                            {selectedRuts.size > 0 && ` · ${selectedRuts.size} seleccionada(s)`}
                            {refreshing && (
                                <span className="ml-2 text-indigo-600 inline-flex items-center gap-1">
                                    <FontAwesomeIcon icon={faSync} spin className="text-[10px]" />
                                    actualizando…
                                </span>
                            )}
                        </span>
                        {pinnedRuts.size > 0 && (
                            <button
                                type="button"
                                onClick={clearPinned}
                                className="text-indigo-600 hover:text-indigo-800 underline"
                            >
                                Quitar {pinnedRuts.size} fijada(s)
                            </button>
                        )}
                    </p>
                </div>

                {isAdmin && (
                    <div className="p-4 sm:p-5 bg-slate-50/80 space-y-4">
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => handleEjecutarScraping('todas')}
                                disabled={isScrapingRunning}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                            >
                                <FontAwesomeIcon icon={faPlay} />
                                Consultar todas
                            </button>
                            <button
                                type="button"
                                onClick={() => handleEjecutarScraping('filtradas')}
                                disabled={isScrapingRunning || !selectedEstado}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                                Consultar filtradas ({filteredEstados.length})
                            </button>
                            <button
                                type="button"
                                onClick={() => handleEjecutarScraping('seleccionadas')}
                                disabled={isScrapingRunning || selectedRuts.size === 0}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                                <FontAwesomeIcon icon={faCheckDouble} />
                                Consultar seleccionadas ({selectedRuts.size})
                            </button>
                            {isScrapingRunning ? (
                                <button
                                    type="button"
                                    onClick={handleDetenerScraping}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                                >
                                    <FontAwesomeIcon icon={faStop} />
                                    {scrapingProgress
                                        ? `En curso ${logStats.procesados}/${logStats.total || '…'}`
                                        : 'Detener seguimiento'}
                                </button>
                            ) : null}
                        </div>

                        <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-slate-200">
                            <div className="flex-1 min-w-[220px] max-w-md">
                                <label className="block text-xs font-semibold text-slate-600 mb-2">Comenzar desde empresa</label>
                                <select
                                    value={desdeRut}
                                    onChange={e => setDesdeRut(e.target.value)}
                                    disabled={isScrapingRunning}
                                    className={fieldClass}
                                >
                                    <option value="">— Desde el inicio —</option>
                                    {clientesRenta.map(c => (
                                        <option key={c._id} value={c.rut}>{c.nombre} ({formatRut(c.rut)})</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleEjecutarScraping('desde')}
                                disabled={isScrapingRunning || !desdeRut}
                                className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                                Ejecutar desde aquí
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Tabla */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead>
                            <tr className="bg-slate-50">
                                {isAdmin && (
                                    <th className="w-10 px-4 py-3">
                                        <input
                                            type="checkbox"
                                            checked={allFilteredSelected}
                                            onChange={toggleSelectAll}
                                            className="rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                                            title="Seleccionar visibles"
                                        />
                                    </th>
                                )}
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Estado</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Empresa</th>
                                <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">RUT</th>
                                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actualización</th>
                                <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Años</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredEstados.map((cliente) => {
                                const añoEstado = cliente.estados?.find(e => e.año === selectedYear);
                                const estadoKey = añoEstado?.estado || 'no_declarada';
                                const isSelected = selectedRuts.has(cliente.rut);
                                const isConsultando = consultandoRut === cliente.rut;
                                const isProcessing = isScrapingRunning && processingNombre && cliente.nombre === processingNombre;
                                const justChanged = recentlyChangedRuts.has(cliente.rut);
                                const fueraDeFiltro = selectedEstado && estadoKey !== selectedEstado && pinnedRuts.has(cliente.rut);

                                let rowClass = 'transition-colors duration-500 ';
                                if (isProcessing) rowClass += 'bg-amber-50 ring-1 ring-inset ring-amber-200';
                                else if (justChanged) rowClass += 'bg-emerald-50 ring-1 ring-inset ring-emerald-200';
                                else if (isSelected) rowClass += 'bg-slate-50/80';
                                else rowClass += 'hover:bg-slate-50/50';

                                return (
                                    <tr key={cliente._id} className={rowClass}>
                                        {isAdmin && (
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelect(cliente.rut)}
                                                    className="rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                                                />
                                            </td>
                                        )}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <EstadoBadge estadoKey={estadoKey} />
                                        </td>
                                        <td className="px-4 py-3 min-w-[220px] sm:min-w-0">
                                            <div className="flex items-start gap-2">
                                                <div className="min-w-0 flex-1">
                                                    <span className="block text-sm font-medium text-slate-900 break-words line-clamp-2 sm:line-clamp-1">
                                                        {cliente.nombre}
                                                    </span>
                                                    <span className="mt-1 block text-xs font-mono text-slate-500 sm:hidden">
                                                        {formatRut(cliente.rut)}
                                                    </span>
                                                </div>
                                                {isProcessing && (
                                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                                        En curso
                                                    </span>
                                                )}
                                                {justChanged && !isProcessing && (
                                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                                                        Actualizado
                                                    </span>
                                                )}
                                                {fueraDeFiltro && (
                                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">
                                                        Nuevo estado
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap text-sm text-slate-600 font-mono">
                                            {formatRut(cliente.rut)}
                                        </td>
                                        <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap text-xs text-slate-500">
                                            {cliente.ultimaActualizacion
                                                ? new Date(cliente.ultimaActualizacion).toLocaleString('es-CL')
                                                : '—'}
                                        </td>
                                        <td className="hidden sm:table-cell px-4 py-3 text-center text-sm text-slate-600 tabular-nums">
                                            {cliente.estados?.length || 0}
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <div className="inline-flex items-center gap-2">
                                                {isAdmin && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleConsultarUna(cliente)}
                                                        disabled={isScrapingRunning || isConsultando}
                                                        className="text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-40"
                                                        title="Consultar solo esta empresa"
                                                    >
                                                        {isConsultando ? (
                                                            <FontAwesomeIcon icon={faSync} spin />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faSync} />
                                                        )}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => openModal(cliente)}
                                                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                                                >
                                                    <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                                                    Detalle
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {filteredEstados.length === 0 && (
                        <div className="text-center py-16 text-sm text-slate-500">No hay resultados para los filtros aplicados</div>
                    )}
                </div>
            </div>

            {/* Modal */}
            <ReactModal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                className="w-full max-w-5xl mx-auto mt-8 mb-8 bg-white rounded-xl shadow-xl border border-slate-200 outline-none max-h-[92vh] overflow-y-auto"
                overlayClassName="fixed inset-0 bg-slate-900/50 flex items-start justify-center p-4 sm:p-6 z-50"
            >
                {selectedCliente && (
                    <div className="p-6 sm:p-8">
                        <div className="flex justify-between items-start mb-6 pb-4 border-b border-slate-100">
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900">{selectedCliente.nombre}</h2>
                                <p className="text-sm text-slate-500 font-mono mt-1">{formatRut(selectedCliente.rut)}</p>
                            </div>
                            <button type="button" onClick={closeModal} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
                        </div>

                        <div className="space-y-5">
                            {selectedCliente.estados?.map((estado) => (
                                <div key={estado.año} className="border border-slate-200 rounded-xl p-5 bg-slate-50/40">
                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                        <h3 className="text-sm font-semibold text-slate-800">Año {estado.año}</h3>
                                        <EstadoBadge estadoKey={estado.estado} size="lg" />
                                    </div>

                                    {estado.mensajeSituacion && (
                                        <div className="mb-3 rounded-md bg-slate-50 px-3 py-2">
                                            <p className="text-xs font-medium text-slate-500 mb-1">Situación</p>
                                            <p className="text-sm text-slate-700">{estado.mensajeSituacion}</p>
                                        </div>
                                    )}

                                    {estado.ultimoEvento?.descripcion && (
                                        <div className="mb-3 rounded-md bg-indigo-50/50 px-3 py-2">
                                            <p className="text-xs font-medium text-indigo-800 mb-1">Último evento · {estado.ultimoEvento.fecha}</p>
                                            <p className="text-sm text-indigo-900">{estado.ultimoEvento.descripcion}</p>
                                        </div>
                                    )}

                                    {estado.historial?.length > 0 && (
                                        <div>
                                            <p className="text-xs font-medium text-slate-500 mb-2">Historial</p>
                                            <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                                                <table className="min-w-full text-sm">
                                                    <thead className="bg-slate-50 sticky top-0">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left font-medium text-slate-500">Fecha</th>
                                                            <th className="px-3 py-2 text-left font-medium text-slate-500">Descripción</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {estado.historial.map((evento, idx) => (
                                                            <tr key={idx}>
                                                                <td className="px-3 py-2 whitespace-nowrap text-slate-500">{evento.fecha}</td>
                                                                <td className="px-3 py-2 text-slate-700">{evento.descripcion}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    <p className="mt-3 text-xs text-slate-400">
                                        Consultado: {estado.fechaConsulta ? new Date(estado.fechaConsulta).toLocaleString('es-CL') : '—'}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            {isAdmin && (
                                <button
                                    type="button"
                                    onClick={() => { closeModal(); handleConsultarUna(selectedCliente); }}
                                    disabled={isScrapingRunning}
                                    className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                                >
                                    Consultar esta empresa
                                </button>
                            )}
                            <button type="button" onClick={closeModal} className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">
                                Cerrar
                            </button>
                        </div>
                    </div>
                )}
            </ReactModal>
        </div>
    );
};

export default EstadosRenta;
